import { forwardRef, useEffect } from "react";
import { useTranslate } from "@refinedev/core";
import { FloatingMenu, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Placeholder from "@tiptap/extension-placeholder";
import { createLowlight } from "lowlight";
import { Stack, FormControl, FormHelperText, Typography, Box } from "@mui/material";

import js from "highlight.js/lib/languages/javascript";
import "highlight.js/styles/tokyo-night-dark.css";

import {
  RxFontBold,
  RxFontItalic,
  RxStrikethrough,
  RxQuote,
  RxCode,
} from "react-icons/rx";

import { EditorProps } from "./types";
import { BubbleButton, BubbleMenu, EditorContent, FloatingMenuButton } from "./styles";

const lowlight = createLowlight({ js });

export const Editor = forwardRef<HTMLDivElement, EditorProps>(
  ({ name, label, value, error, helperText, onChange, minHeight = 200 }, ref) => {
    const translate = useTranslate();

    const editor = useEditor({
      extensions: [
        StarterKit,
        CodeBlockLowlight.configure({
          lowlight,
        }),
        Placeholder.configure({
          placeholder: translate("components.editor.placeholder"),
        }),
      ],
      content: value,
      onUpdate: ({ editor }) => {
        if (!onChange) return;
        const html = editor.getHTML();
        onChange(html);
      },
    });

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value || "");
      }
    }, [value, editor]);

    const removeSlash = () => {
      editor?.chain().focus().deleteRange({
        from: editor.state.selection.from - 1,
        to: editor.state.selection.from,
      }).run();
    };

    return (
      <Box sx={{ width: '100%' }}>
        <FormControl fullWidth error={!!error}>
          <Stack spacing={0.5}>
            {label && (
              <Typography variant="body2">
                {label}
              </Typography>
            )}

            <Box sx={{ width: '100%' }}>
              <EditorContent
                ref={ref}
                name={name}
                editor={editor}
                $minHeight={minHeight}
              />
            </Box>

            {error && helperText && (
              <FormHelperText>{helperText}</FormHelperText>
            )}
          </Stack>

          {editor && (
            <FloatingMenu
              editor={editor}
              shouldShow={({ state }) => {
                const { $from } = state.selection;
                const currentLineText = $from.nodeBefore?.textContent;
                return currentLineText === "/";
              }}
            >
              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleHeading({ level: 1 }).run();
                }}
                imgURL="https://www.notion.so/images/blocks/header.57a7576a.png"
                title={translate("components.editor.heading1.title")}
                description={translate("components.editor.heading1.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleHeading({ level: 2 }).run();
                }}
                imgURL="https://www.notion.so/images/blocks/subheader.9aab4769.png"
                title={translate("components.editor.heading2.title")}
                description={translate("components.editor.heading2.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleHeading({ level: 3 }).run();
                }}
                imgURL="https://www.notion.so/images/blocks/subsubheader.d0ed0bb3.png"
                title={translate("components.editor.heading3.title")}
                description={translate("components.editor.heading3.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleBulletList().run();
                }}
                imgURL="https://www.notion.so/images/blocks/bulleted-list.0e87e917.png"
                title={translate("components.editor.bulletList.title")}
                description={translate("components.editor.bulletList.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleOrderedList().run();
                }}
                imgURL="https://www.notion.so/images/blocks/numbered-list.0406affe.png"
                title={translate("components.editor.orderedList.title")}
                description={translate("components.editor.orderedList.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleBlockquote().run();
                }}
                imgURL="https://www.notion.so/images/blocks/quote/en-US.png"
                title={translate("components.editor.blockquote.title")}
                description={translate("components.editor.blockquote.description")}
              />

              <FloatingMenuButton
                onClick={() => {
                  removeSlash();
                  editor.chain().focus().toggleCodeBlock().run();
                }}
                imgURL="https://www.notion.so/images/blocks/text/en-US.png"
                title={translate("components.editor.codeBlock.title")}
                description={translate("components.editor.codeBlock.description")}
              />
            </FloatingMenu>
          )}

          {editor && (
            <BubbleMenu editor={editor}>
              <BubbleButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                data-active={editor.isActive("bold")}
              >
                <RxFontBold />
              </BubbleButton>

              <BubbleButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                data-active={editor.isActive("italic")}
              >
                <RxFontItalic />
              </BubbleButton>

              <BubbleButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                data-active={editor.isActive("strike")}
              >
                <RxStrikethrough />
              </BubbleButton>

              <BubbleButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                data-active={editor.isActive("blockquote")}
              >
                <RxQuote />
              </BubbleButton>

              <BubbleButton
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                data-active={editor.isActive("codeBlock")}
              >
                <RxCode />
              </BubbleButton>
            </BubbleMenu>
          )}
        </FormControl>
      </Box>
    );
  }
);

Editor.displayName = "Editor";
